import { useState } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SEO from '../../components/feature/SEO';
import ContactForm from './components/ContactForm';
import VenderForm from './components/VenderForm';
import AlquilarForm from './components/AlquilarForm';
import AppointmentForm from './components/AppointmentForm';
import { useSettings } from '../../hooks/useSettings';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactoJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contacto – Taller Mecánico Kevin Alberto Ronda',
    url: `${siteUrl}/contacto`,
    description:
      'Contacta con el Taller Mecánico Kevin Alberto en Ronda, Málaga. Pide presupuesto, reserva cita o vende tu coche. Respuesta en menos de 24h.',
    inLanguage: 'es',
    isPartOf: { '@type': 'WebSite', url: siteUrl },
    mainEntity: {
      '@type': 'AutoRepair',
      name: 'Taller Mecánico Kevin Alberto',
      telephone: '+34666365922',
      email: 'tallerkevinalberto@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'C. Guadalimar, 7',
        addressLocality: 'Ronda',
        postalCode: '29400',
        addressRegion: 'Málaga',
        addressCountry: 'ES',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '07:00',
          closes: '15:00',
        },
      ],
    },
  },
];

type Tab = 'presupuesto' | 'cita' | 'vender' | 'alquiler';

const TABS: { id: Tab; icon: string; label: string }[] = [
  { id: 'presupuesto', icon: 'ri-tools-line', label: 'Pide presupuesto' },
  { id: 'cita', icon: 'ri-calendar-check-line', label: 'Reservar cita' },
  { id: 'vender', icon: 'ri-car-line', label: 'Vender mi coche' },
  { id: 'alquiler', icon: 'ri-steering-2-line', label: 'Alquilar coche' },
];

const ContactoPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('presupuesto');
  const { settings } = useSettings();

  const contactInfo = [
    { icon: 'ri-phone-line', title: 'Teléfono', href: `tel:${settings.telefono}`, text: settings.telefono },
    { icon: 'ri-whatsapp-line', title: 'WhatsApp', href: `https://wa.me/${settings.whatsapp.replace(/\D/g, '')}`, text: settings.whatsapp, ext: true },
    { icon: 'ri-mail-line', title: 'Email', href: `mailto:${settings.email}`, text: settings.email },
    { icon: 'ri-map-pin-line', title: 'Dirección', href: '#', text: `${settings.direccion}, ${settings.ciudad}` },
  ];

  return (
    <PageLayout>
      <SEO
        title="Contacto Taller Mecánico Ronda Málaga | Presupuesto y Cita Kevin Alberto"
        description="Contacta con Taller Kevin Alberto en Ronda, Málaga. Pide presupuesto sin compromiso, reserva cita online o vende tu coche. Respuesta en menos de 24h. Tel: +34 666 36 59 22."
        keywords="contacto taller mecánico Ronda, presupuesto mecánico Ronda, cita taller Ronda Málaga, teléfono taller Kevin Alberto"
        canonical="/contacto"
        jsonLd={contactoJsonLd}
      />
      <PageHero
        tag="Hablemos"
        title="Contacta"
        titleAccent="con nosotros"
        subtitle="Presupuesto sin compromiso en menos de 24h. Llámanos, escríbenos o ven directamente al taller."
        breadcrumb="Contacto"
      />

      <section className="bg-zinc-950 py-14 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Left panel: tabs + forms */}
          <div className="lg:col-span-3">
            {/* Tab switcher */}
            <div className="flex gap-0 mb-0">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 text-[11px] font-black uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? 'bg-zinc-900 border-yellow-400 text-yellow-400'
                      : 'bg-zinc-900/40 border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/70'
                  }`}
                >
                  <i className={tab.icon} />
                  <span className="hidden sm:block">{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="bg-zinc-900 border border-zinc-800 border-t-0 p-6 md:p-8">
              {activeTab === 'presupuesto' && <ContactForm />}
              {activeTab === 'cita' && <AppointmentForm />}
              {activeTab === 'vender' && <VenderForm />}
              {activeTab === 'alquiler' && <AlquilarForm />}
            </div>
          </div>

          {/* Right info panel */}
          <div className="lg:col-span-2 space-y-4">
            {contactInfo.map(({ icon, title, href, text, ext }) => (
              <div key={title} className="bg-zinc-900 border border-zinc-800 p-5 flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 flex-shrink-0">
                  <i className={`${icon} text-sm`} />
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">{title}</p>
                  <a href={href} target={ext ? '_blank' : undefined} rel="noopener noreferrer"
                    className="text-white font-bold text-sm hover:text-yellow-400 transition-colors cursor-pointer block">
                    {text}
                  </a>
                </div>
              </div>
            ))}

            <div className="bg-zinc-900 border border-zinc-800 p-5">
              <p className="text-zinc-500 text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
                <i className="ri-time-line text-yellow-400" />Horario
              </p>
              {[
                { day: 'Lunes — Viernes', hours: settings.horario_semana },
                { day: 'Sábados', hours: settings.horario_sabado },
                { day: 'Domingos', hours: 'Cerrado' },
              ].map(({ day, hours }) => (
                <div key={day} className="flex justify-between items-center py-1.5 border-b border-zinc-800 last:border-0">
                  <span className="text-zinc-400 text-xs">{day}</span>
                  <span className={`text-xs font-bold ${hours === 'Cerrado' ? 'text-red-400' : 'text-white'}`}>{hours}</span>
                </div>
              ))}
            </div>

            <div className="overflow-hidden border border-zinc-800 h-48">
              <iframe
                title="Bali Garage ubicación"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96739.17694882607!2d-0.4546874!3d39.4561165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd604f4cf0efztb3%3A0x10100b25564da7f!2sMalaga%2C%20Spain!5e0!3m2!1ses!2ses!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(1) invert(0.9)' }}
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ContactoPage;
